import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { Button } from './ui/button';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Cookie, Settings } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'bookingflows_cookie_consent';
const GA_MEASUREMENT_ID = 'G-LVBV3VDFQM'; // BookingFlows platform GA4

// Banner shows ONLY on public marketing pages — signed-in app areas, tickets,
// widgets, check-in and portals never show it.
const MARKETING_PATHS = new Set([
  '/', '/for-suppliers', '/for-charters', '/for-tours', '/for-rentals',
  '/marketplace', '/search', '/marketplace/search', '/blog',
  '/privacy', '/terms', '/meet-the-team', '/demo', '/onboarding',
]);
const MARKETING_PREFIXES = ['/blog/', '/customers/', '/tours-in/'];
const isMarketingPath = (p) => MARKETING_PATHS.has(p) || MARKETING_PREFIXES.some((pre) => p.startsWith(pre));

// Load Google Analytics only when consent is given
const loadGoogleAnalytics = (measurementId) => {
  if (!measurementId || measurementId === 'G-XXXXXXXXXX') {
    return;
  }

  // Check if already loaded
  if (window.gtag) {
    return;
  }

  // Load gtag.js script
  const script = document.createElement('script');
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
  script.async = true;
  document.head.appendChild(script);

  // Initialize gtag
  window.dataLayer = window.dataLayer || [];
  window.gtag = function() { window.dataLayer.push(arguments); };
  window.gtag('js', new Date());
  window.gtag('config', measurementId, {
    anonymize_ip: true, // GDPR compliance
    cookie_flags: 'SameSite=None;Secure'
  });

};

// Disable Google Analytics
const disableGoogleAnalytics = () => {
  if (GA_MEASUREMENT_ID && GA_MEASUREMENT_ID !== 'G-XXXXXXXXXX') {
    window[`ga-disable-${GA_MEASUREMENT_ID}`] = true;
  }
};

// Check and apply existing consent on app load
export const applyExistingConsent = () => {
  try {
    const stored = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (stored) {
      const consent = JSON.parse(stored);
      if (consent.accepted && consent.preferences?.analytics) {
        loadGoogleAnalytics(GA_MEASUREMENT_ID);
      } else {
        disableGoogleAnalytics();
      }
    }
  } catch (e) {
    console.error('Error applying consent:', e);
  }
};

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [showPreferences, setShowPreferences] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: true,
    marketing: false
  });

  const { pathname } = useLocation();

  useEffect(() => {
    if (!isMarketingPath(pathname)) {
      // Not a marketing page — never show the banner here, but still honour
      // an existing consent choice (keeps analytics state consistent).
      setIsVisible(false);
      setIsAnimating(false);
      if (localStorage.getItem(COOKIE_CONSENT_KEY)) applyExistingConsent();
      return;
    }

    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show banner after 1 second delay
      const timer = setTimeout(() => {
        setIsVisible(true);
        setTimeout(() => setIsAnimating(true), 50);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      // Apply existing consent
      applyExistingConsent();
    }
  }, [pathname]);

  const saveConsent = (accepted, prefs) => {
    const consentData = {
      accepted,
      timestamp: new Date().toISOString(),
      preferences: prefs,
      version: '1.0'
    };

    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(consentData));

    // Apply consent immediately
    if (accepted && prefs.analytics) {
      loadGoogleAnalytics(GA_MEASUREMENT_ID);
    } else {
      disableGoogleAnalytics();
    }

    closeConsent();
  };

  const handleAcceptAll = () => {
    saveConsent(true, { necessary: true, analytics: true, marketing: true });
  };

  const handleDecline = () => {
    saveConsent(false, { necessary: true, analytics: false, marketing: false });
  };

  const handleSavePreferences = () => {
    saveConsent(preferences.analytics || preferences.marketing, preferences);
  };

  const closeConsent = () => {
    setShowPreferences(false);
    setIsAnimating(false);
    setTimeout(() => setIsVisible(false), 300);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Slim consent bar (desktop) / compact card (mobile). Hidden while the
          preferences dialog is open so it never sits above the modal overlay. */}
      {!showPreferences && (
        <div
          className={`fixed bottom-4 left-4 right-auto z-[9998] w-[calc(100vw-2rem)] max-w-[290px] sm:max-w-xs transition-all duration-300 ease-out ${
            isAnimating ? 'translate-y-0 opacity-100' : 'translate-y-3 opacity-0 pointer-events-none'
          }`}
          style={{ marginBottom: 'env(safe-area-inset-bottom, 0px)' }}
          data-testid="cookie-consent-banner"
        >
          {/* Subtle corner pill — compliant (accept / decline / manage) but out of the way */}
          <div className="bg-white rounded-2xl shadow-xl ring-1 ring-gray-200/70 p-3.5">
            <div className="flex items-start gap-2.5">
              <Cookie className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="min-w-0 flex-1">
                <p className="text-xs text-gray-600 leading-relaxed">
                  We use cookies to enhance your experience.{' '}
                  <a href="/privacy" className="text-blue-600 hover:underline">Privacy</a>
                </p>
                <div className="flex items-center gap-2 mt-2.5">
                  <Button
                    size="sm"
                    onClick={handleAcceptAll}
                    className="h-7 px-3 text-xs bg-blue-600 hover:bg-blue-700 text-white"
                    data-testid="cookie-accept-all"
                  >
                    Accept
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleDecline}
                    className="h-7 px-3 text-xs text-gray-600 border-gray-300"
                    data-testid="cookie-decline"
                  >
                    Decline
                  </Button>
                  <button
                    onClick={() => setShowPreferences(true)}
                    className="ml-auto text-gray-400 hover:text-gray-600 transition-colors p-1"
                    aria-label="Manage cookie preferences"
                    data-testid="cookie-manage"
                  >
                    <Settings className="w-3.5 h-3.5" />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Preferences — shared Dialog component (overlay, focus trap & Esc handled for us) */}
      <Dialog open={showPreferences} onOpenChange={setShowPreferences}>
        <DialogContent className="max-w-md" data-testid="cookie-preferences-dialog">
          <DialogHeader>
            <DialogTitle>Cookie preferences</DialogTitle>
            <DialogDescription>
              Essential cookies are always active for basic functionality.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-3">
            {/* Essential — always on */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <h4 className="font-medium text-gray-900 text-sm">Essential</h4>
                <p className="text-xs text-gray-500">Required for site functionality</p>
              </div>
              <span className="text-xs font-medium text-green-600 bg-green-100 px-2 py-1 rounded-md">Always on</span>
            </div>

            {/* Analytics */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1 mr-4">
                <h4 className="font-medium text-gray-900 text-sm">Analytics</h4>
                <p className="text-xs text-gray-500">Understand site usage</p>
              </div>
              <Switch
                checked={preferences.analytics}
                onCheckedChange={(v) => setPreferences({ ...preferences, analytics: v })}
                aria-label="Toggle analytics cookies"
                data-testid="cookie-toggle-analytics"
              />
            </div>

            {/* Marketing */}
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex-1 mr-4">
                <h4 className="font-medium text-gray-900 text-sm">Marketing</h4>
                <p className="text-xs text-gray-500">Personalized ads</p>
              </div>
              <Switch
                checked={preferences.marketing}
                onCheckedChange={(v) => setPreferences({ ...preferences, marketing: v })}
                aria-label="Toggle marketing cookies"
                data-testid="cookie-toggle-marketing"
              />
            </div>
          </div>

          <p className="text-xs text-gray-500 text-center">
            We don&apos;t sell your data. <a href="/privacy" className="text-blue-600 underline">Privacy Policy</a>
          </p>

          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={handleDecline}
              className="flex-1"
              data-testid="cookie-reject-all"
            >
              Reject all
            </Button>
            <Button
              onClick={handleSavePreferences}
              className="flex-1 bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="cookie-save-preferences"
            >
              Save preferences
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default CookieConsent;
